<template>
  <div
    :class="
      classes(
        n(),
        {
          [n('--disabled')]: mergedDisabled,
          [n('--focused')]: isFocused,
          [n('--in-form-item')]: inFormItem,
        }
      )
    "
  >
    <div :class="n('wrapper')">
      <textarea
        ref="textareaRef"
        :class="n('native')"
        :value="innerValue"
        :placeholder="placeholder"
        :placeholder-class="n('placeholder')"
        :disabled="mergedDisabled"
        v-bind="readonly ? { readonly: true } : {}"
        :maxlength="maxlength"
        :rows="rows"
        :name="name"
        :autofocus="autofocus"
        :adjust-position="adjustPosition"
        :style="textareaStyle"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
        @keydown="handleKeydown"
        @keyup="handleKeyup"
      />
      <!-- 字数统计（在横线上方右下角） -->
      <div
        v-if="showWordLimit && maxlength"
        :class="n('word-limit')"
      >
        {{ String(innerValue || '').length }}/{{ maxlength }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, nextTick, inject } from 'virtual-vue';
import { props } from './props';
import { emits } from './emits';
import { createNamespace } from '../utils/components';
import { useVModel } from '../hooks/use-v-model';
import { FORM_ITEM_KEY } from '../form/constants';

const { name, n, classes } = createNamespace('textarea');

export default defineComponent({
  name,
  props,
  emits,
  setup(props, { emit }) {
    const textareaRef = ref<HTMLTextAreaElement>();
    const isFocused = ref(false);
    const innerValue = useVModel<string | number>(props);

    const formItemContext = inject(FORM_ITEM_KEY, null);
    const inFormItem = computed(() => !!formItemContext);

    const mergedDisabled = computed(() => {
      if (formItemContext) return formItemContext.disabled.value || props.disabled;
      return props.disabled;
    });

    const textareaStyle = computed(() => {
      if (!props.autosize) {
        return {};
      }
      return {
        resize: 'none' as const,
        overflow: 'hidden' as const,
      };
    });

    const adjustHeight = () => {
      if (!props.autosize || !textareaRef.value) {
        return;
      }

      const textarea = textareaRef.value;
      textarea.style.height = 'auto';

      const autosizeConfig = props.autosize;
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight, 10) || 24;

      if (typeof autosizeConfig === 'object' && autosizeConfig) {
        const config = autosizeConfig as { minRows?: number; maxRows?: number };
        const minRows = config.minRows ?? 1;
        const maxRows = config.maxRows ?? Infinity;
        const minHeight = lineHeight * minRows;
        const maxHeight = maxRows === Infinity ? Infinity : lineHeight * maxRows;

        textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)}px`;
      } else {
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    };

    const handleInput = (event: Event) => {
      const target = event.target as HTMLTextAreaElement;
      innerValue.value = target.value;

      nextTick(() => {
        adjustHeight();
      });
    };

    const handleFocus = (event: FocusEvent) => {
      isFocused.value = true;
      formItemContext?.onControlFocus?.();
      emit('focus', event);
    };

    const handleBlur = (event: FocusEvent) => {
      isFocused.value = false;
      formItemContext?.onControlBlur?.();
      emit('blur', event);
    };

    const handleChange = (event: Event) => {
      const target = event.target as HTMLTextAreaElement;
      emit('change', target.value);
    };

    const handleKeydown = (event: KeyboardEvent) => {
      emit('keydown', event);
    };

    const handleKeyup = (event: KeyboardEvent) => {
      emit('keyup', event);
    };

    const focus = () => {
      if (textareaRef.value) {
        textareaRef.value.focus();
      }
    };

    const blur = () => {
      if (textareaRef.value) {
        textareaRef.value.blur();
      }
    };

    watch(
      () => innerValue.value,
      () => {
        nextTick(() => {
          adjustHeight();
        });
      },
    );

    onMounted(() => {
      nextTick(() => {
        adjustHeight();
      });
    });

    return {
      n,
      classes,
      textareaRef,
      isFocused,
      innerValue,
      inFormItem,
      mergedDisabled,
      textareaStyle,
      handleInput,
      handleFocus,
      handleBlur,
      handleChange,
      handleKeydown,
      handleKeyup,
      focus,
      blur,
    };
  },
});
</script>
